/**
 * Board Findings Panel — reverse-chronological findings table with flag
 * badges, right slider detail panel, document type & flag category filters.
 *
 * Reads from the `boardReportFindings` table. Updates incrementally.
 * Clicking a row opens a BoardFindingPanel sheet with full details.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { Loader2, ClipboardList, ChevronRight } from "lucide-react";
import { cn } from "@/utils/misc";
import type { BoardReportFindingUI } from "./types";
import { BoardFindingPanel } from "./BoardFindingPanel";
import { useFlagDismissals, flagGlowClass } from "../flags";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FLAG_CONFIG: Record<
  string,
  {
    label: string;
    color: string;
    bg: string;
    darkBg: string;
    darkColor: string;
  }
> = {
  financial_distress: {
    label: "Financial distress",
    color: "text-red-700",
    bg: "bg-red-50 border-red-200",
    darkBg: "dark:bg-red-950 dark:border-red-800",
    darkColor: "dark:text-red-300",
  },
  turnaround: {
    label: "Turnaround",
    color: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
    darkBg: "dark:bg-amber-950 dark:border-amber-800",
    darkColor: "dark:text-amber-300",
  },
  safe_harbour: {
    label: "Safe harbour",
    color: "text-blue-700",
    bg: "bg-blue-50 border-blue-200",
    darkBg: "dark:bg-blue-950 dark:border-blue-800",
    darkColor: "dark:text-blue-300",
  },
  material_transaction: {
    label: "Material transaction",
    color: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
    darkBg: "dark:bg-emerald-950 dark:border-emerald-800",
    darkColor: "dark:text-emerald-300",
  },
};

const DOC_TYPE_LABELS: Record<string, string> = {
  board_minutes: "Minutes",
  board_report: "Report",
  circular_resolution: "Resolution",
  pre_appointment_minutes: "Pre-appt",
};

const CONFIDENCE_CONFIG: Record<
  string,
  {
    label: string;
    color: string;
    bg: string;
    darkBg: string;
    darkColor: string;
  }
> = {
  high: {
    label: "High",
    color: "text-emerald-700",
    bg: "bg-emerald-50 border-emerald-200",
    darkBg: "dark:bg-emerald-950 dark:border-emerald-800",
    darkColor: "dark:text-emerald-300",
  },
  medium: {
    label: "Med",
    color: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
    darkBg: "dark:bg-amber-950 dark:border-amber-800",
    darkColor: "dark:text-amber-300",
  },
  low: {
    label: "Low",
    color: "text-red-700",
    bg: "bg-red-50 border-red-200",
    darkBg: "dark:bg-red-950 dark:border-red-800",
    darkColor: "dark:text-red-300",
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(d: string): string {
  try {
    return new Date(d).toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

function FlagBadge({ type, dismissed }: { type: string; dismissed?: boolean }) {
  const cfg = FLAG_CONFIG[type];
  if (!cfg) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] font-medium",
        cfg.bg,
        cfg.color,
        cfg.darkBg,
        cfg.darkColor,
        dismissed ? "line-through opacity-40" : flagGlowClass(true),
      )}
    >
      {cfg.label}
    </span>
  );
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: string;
}) {
  return (
    <div className="min-w-0 rounded-xl border bg-muted/30 px-4 py-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-0.5 text-2xl font-semibold",
          accent ?? "text-foreground",
        )}
      >
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function BoardFindingsPanel({
  jobId,
  isRunning,
  isViewer: _isViewer,
}: {
  jobId: Id<"jobs">;
  isRunning: boolean;
  isViewer?: boolean;
}) {
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [activeFlag, setActiveFlag] = useState<string | null>(null);
  const [activeDocType, setActiveDocType] = useState<string | null>(null);

  const flagCtx = useFlagDismissals(jobId);

  const { data: allEntries } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listBoardReportFindingsForJob,
      { jobId },
    ),
  );

  const entries = useMemo(
    () => (allEntries ?? []) as BoardReportFindingUI[],
    [allEntries],
  );

  const activeEntries = useMemo(
    () => entries.filter((e) => e.status === "active"),
    [entries],
  );

  // Stats
  const stats = useMemo(
    () => ({
      total: activeEntries.length,
      distress: activeEntries.filter((f) =>
        f.flags.includes("financial_distress"),
      ).length,
      materialTxn: activeEntries.filter((f) =>
        f.flags.includes("material_transaction"),
      ).length,
      safeHarbour: activeEntries.filter((f) => f.flags.includes("safe_harbour"))
        .length,
    }),
    [activeEntries],
  );

  // Available doc types
  const availableDocTypes = useMemo(() => {
    const types = new Set(activeEntries.map((e) => e.documentType));
    return Array.from(types);
  }, [activeEntries]);

  // Filtered + sorted (reverse chronological)
  const filtered = useMemo(() => {
    return activeEntries
      .filter((f) => !activeFlag || f.flags.includes(activeFlag))
      .filter((f) => !activeDocType || f.documentType === activeDocType)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [activeEntries, activeFlag, activeDocType]);

  const selectedEntry = useMemo(
    () => entries.find((e) => e._id === selectedEntryId) ?? null,
    [entries, selectedEntryId],
  );

  // ---------------------------------------------------------------------------
  // Empty / loading states
  // ---------------------------------------------------------------------------

  if (entries.length === 0) {
    if (isRunning) {
      return (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          <p className="text-sm text-muted-foreground">
            Extracting board findings...
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <div className="rounded-full bg-muted p-3">
          <ClipboardList className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          No board report findings yet.
        </p>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Card — wraps stats, filters, and the table so the white surface
         contrasts against the page's bg-muted backdrop. Mirrors the
         Secured Creditors / Preferred Creditors panel treatment. */}
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-3 px-5 pb-4 pt-5">
          <StatCard label="Findings" value={stats.total} />
          <StatCard
            label="Distress flags"
            value={stats.distress}
            accent="text-red-600 dark:text-red-400"
          />
          <StatCard
            label="Material txns"
            value={stats.materialTxn}
            accent="text-emerald-600 dark:text-emerald-400"
          />
          <StatCard
            label="Safe harbour"
            value={stats.safeHarbour}
            accent="text-blue-600 dark:text-blue-400"
          />
        </div>

        {/* Filters */}
        <div className="flex items-end gap-6 px-5 pb-4">
          {/* Doc type filter */}
          <div className="flex-1">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Document type
            </div>
            <div className="flex gap-0 overflow-hidden rounded-md border">
              <button
                type="button"
                onClick={() => setActiveDocType(null)}
                className={cn(
                  "flex-1 border-r px-2 py-1.5 text-[11px] font-medium transition-colors",
                  !activeDocType
                    ? "bg-foreground text-background"
                    : "bg-background text-muted-foreground hover:bg-muted/50",
                )}
              >
                All ({activeEntries.length})
              </button>
              {availableDocTypes.map((dt, idx) => {
                const count = activeEntries.filter(
                  (f) => f.documentType === dt,
                ).length;
                return (
                  <button
                    key={dt}
                    type="button"
                    onClick={() =>
                      setActiveDocType(activeDocType === dt ? null : dt)
                    }
                    className={cn(
                      "flex-1 px-2 py-1.5 text-[11px] font-medium transition-colors",
                      idx < availableDocTypes.length - 1 && "border-r",
                      activeDocType === dt
                        ? "bg-foreground text-background"
                        : "bg-background text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {DOC_TYPE_LABELS[dt] ?? dt} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          <div className="w-px self-stretch bg-border" />

          {/* Flag filter */}
          <div className="flex-1">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Flag category
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(FLAG_CONFIG).map(([key, cfg]) => {
                const count = activeEntries.filter((f) =>
                  f.flags.includes(key),
                ).length;
                if (count === 0) return null;
                const isActive = activeFlag === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveFlag(isActive ? null : key)}
                    className={cn(
                      "rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors",
                      isActive
                        ? cn(
                            cfg.bg,
                            cfg.color,
                            cfg.darkBg,
                            cfg.darkColor,
                            "border-current",
                          )
                        : "border-border bg-background text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {cfg.label} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Info bar */}
        <div className="flex items-center justify-between border-t border-border px-5 pb-3 pt-3 text-xs text-muted-foreground">
          <span>
            Showing {filtered.length} of {activeEntries.length} findings
          </span>
          {(activeFlag || activeDocType) && (
            <button
              type="button"
              onClick={() => {
                setActiveFlag(null);
                setActiveDocType(null);
              }}
              className="rounded bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted/80"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Data table */}
        <div className="min-w-0 flex-1 border-t border-border">
          <div className="h-[calc(100vh-560px)] overflow-auto">
            <Table className="w-full table-fixed">
              <colgroup>
                <col className="w-[12%]" />
                <col className="w-[11%]" />
                <col className="w-[39%]" />
                <col className="w-[22%]" />
                <col className="w-[9%]" />
                <col className="w-[7%]" />
              </colgroup>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-transparent">
                  <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Date
                  </TableHead>
                  <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Type
                  </TableHead>
                  <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Summary
                  </TableHead>
                  <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Flags
                  </TableHead>
                  <TableHead className="h-10 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Conf.
                  </TableHead>
                  <TableHead className="h-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-32 text-center">
                      <span className="text-sm text-muted-foreground">
                        No findings match the current filters.
                      </span>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((finding) => {
                    const isSelected = selectedEntryId === finding._id;
                    const flagDetail = finding.flagDetails ?? "";
                    return (
                      <TableRow
                        key={finding._id}
                        onClick={() => setSelectedEntryId(finding._id)}
                        className={cn(
                          "cursor-pointer transition-colors",
                          isSelected
                            ? "hover:bg-primary/8 bg-primary/5"
                            : "hover:bg-muted/30",
                        )}
                      >
                        <TableCell className="whitespace-nowrap px-4 py-3 text-xs tabular-nums text-muted-foreground">
                          {fmtDate(finding.date)}
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {DOC_TYPE_LABELS[finding.documentType] ??
                              finding.documentType}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="line-clamp-2 text-sm leading-relaxed text-foreground">
                            {finding.summary}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {finding.flags.length > 0 ? (
                              finding.flags.map((f) => (
                                <FlagBadge
                                  key={f}
                                  type={f}
                                  dismissed={flagCtx.isDismissed(
                                    "boardReports",
                                    finding._id,
                                    { type: f, detail: flagDetail },
                                  )}
                                />
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground/40">
                                {"\u2014"}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          {(() => {
                            const cfg = CONFIDENCE_CONFIG[finding.confidence];
                            if (!cfg) return null;
                            return (
                              <span
                                className={cn(
                                  "inline-flex items-center whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] font-medium",
                                  cfg.bg,
                                  cfg.color,
                                  cfg.darkBg,
                                  cfg.darkColor,
                                )}
                              >
                                {cfg.label}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        Click any row to view flag details, parties, and source documents
      </p>

      {/* Detail panel */}
      <BoardFindingPanel
        entry={selectedEntry}
        open={selectedEntry !== null}
        onClose={() => setSelectedEntryId(null)}
        flagCtx={flagCtx}
      />
    </div>
  );
}
